import React, { useState, useEffect } from 'react';
import { Play, CheckCircle, Circle, Calendar, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function EpisodeList({ animeId, onClose }) {
  const { user } = useAuth();
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [watchedEpisodes, setWatchedEpisodes] = useState(new Set());
  const [totalEpisodes, setTotalEpisodes] = useState(0);
  const [episodesReturned, setEpisodesReturned] = useState(0);
  const [showingLatest, setShowingLatest] = useState(false);

  useEffect(() => {
    fetchEpisodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animeId]);

  const fetchEpisodes = async () => {
    try {
      setLoading(true);
      const url = user 
        ? `/api/episodes/user/${user.id}/anime/${animeId}`
        : `/api/episodes/anime/${animeId}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch episodes');
      
      const data = await response.json();
      setEpisodes(data.episodes || []);
      setTotalEpisodes(data.totalEpisodes || (data.episodes ? data.episodes.length : 0));
      setEpisodesReturned(data.episodesReturned || (data.episodes ? data.episodes.length : 0));
      setShowingLatest(!!data.showingLatest);
      
      // Track watched episodes
      if (user && data.episodes) {
        const watched = new Set(
          data.episodes
            .filter(ep => ep.watched)
            .map(ep => ep.episode_id)
        );
        setWatchedEpisodes(watched);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching episodes:', err);
      setError('Failed to load episodes');
    } finally {
      setLoading(false);
    }
  };

  const toggleWatched = async (episodeId) => {
    if (!user) {
      alert('Please log in to track watched episodes');
      return;
    }

    try {
      const isWatched = watchedEpisodes.has(episodeId);
      
      const response = await fetch('/api/episodes/watched', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          episodeId: episodeId,
          watched: !isWatched
        })
      });

      if (!response.ok) throw new Error('Failed to update watch status');

      // Update local state
      const newWatched = new Set(watchedEpisodes);
      if (isWatched) {
        newWatched.delete(episodeId);
      } else {
        newWatched.add(episodeId);
      }
      setWatchedEpisodes(newWatched);
    } catch (err) {
      console.error('Error updating watch status:', err);
      alert('Failed to update watch status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBA';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
        <p className="text-gray-400 mt-4">Loading episodes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 text-center">
        <p className="text-red-400">{error}</p>
        <button
          onClick={fetchEpisodes}
          className="mt-4 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (episodes.length === 0) {
    return (
      <div className="bg-gray-800/60 rounded-lg p-8 text-center">
        <Play size={48} className="mx-auto text-gray-600 mb-4" />
        <p className="text-gray-400 text-lg">No episode information available</p>
        <p className="text-gray-500 text-sm mt-2">Episode data may not be available for this anime yet.</p>
      </div>
    );
  }

  const watchedCount = episodes.filter(ep => watchedEpisodes.has(ep.episode_id)).length;
  const progress = ((watchedCount / episodes.length) * 100).toFixed(0);

  return (
    <div className="space-y-6">
      {/* Info about limited results */}
      {showingLatest && totalEpisodes > episodesReturned && (
        <div className="bg-yellow-900/20 border border-yellow-500/40 rounded-lg p-3 text-center text-sm text-yellow-300">
          Showing latest {episodesReturned} of {totalEpisodes} episodes
        </div>
      )}

      {/* Progress Bar */}
      {user && (
        <div className="bg-gray-800/60 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-400">Your Progress</span>
            <span className="text-sm font-semibold text-emerald-400">
              {watchedCount} / {episodes.length} episodes
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-emerald-500 to-green-400 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1 text-center">{progress}% Complete</p>
        </div>
      )}

      {/* Episode List */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
        {episodes.map((episode) => {
          const isWatched = watchedEpisodes.has(episode.episode_id);
          
          return (
            <div
              key={episode.episode_id}
              className={`bg-gray-800/60 rounded-lg overflow-hidden hover:bg-gray-700/60 transition-all ${
                isWatched ? 'opacity-75' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Episode Thumbnail */}
                {episode.image_url && (
                  <div className="relative flex-shrink-0 w-40 h-24 bg-gray-900 rounded-lg overflow-hidden">
                    <img
                      src={episode.image_url}
                      alt={`Episode ${episode.episode_number} screenshot`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Watch Status Overlay */}
                    {user && (
                      <button
                        onClick={() => toggleWatched(episode.episode_id)}
                        className="absolute top-2 right-2 hover:scale-110 transition-transform bg-black/60 rounded-full p-1"
                        title={isWatched ? 'Mark as unwatched' : 'Mark as watched'}
                      >
                        {isWatched ? (
                          <CheckCircle size={20} className="text-emerald-500" />
                        ) : (
                          <Circle size={20} className="text-gray-300" />
                        )}
                      </button>
                    )}
                  </div>
                )}

                {/* Episode Info */}
                <div className="flex-1 min-w-0 p-4">
                  <div className="flex items-start gap-3">
                    {/* Watch Status Button (for episodes without images) */}
                    {!episode.image_url && user && (
                      <button
                        onClick={() => toggleWatched(episode.episode_id)}
                        className="flex-shrink-0 mt-1 hover:scale-110 transition-transform"
                        title={isWatched ? 'Mark as unwatched' : 'Mark as watched'}
                      >
                        {isWatched ? (
                          <CheckCircle size={24} className="text-emerald-500" />
                        ) : (
                          <Circle size={24} className="text-gray-600" />
                        )}
                      </button>
                    )}

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-emerald-400 font-bold text-sm">
                          Episode {episode.episode_number}
                        </span>
                        {episode.filler && (
                          <span className="px-2 py-0.5 bg-yellow-500/20 border border-yellow-500/50 rounded text-xs text-yellow-400">
                            Filler
                          </span>
                        )}
                        {episode.recap && (
                          <span className="px-2 py-0.5 bg-blue-500/20 border border-blue-500/50 rounded text-xs text-blue-400">
                            Recap
                          </span>
                        )}
                      </div>

                      <h4 className="text-white font-medium mb-2 line-clamp-1">
                        {episode.title || `Episode ${episode.episode_number}`}
                      </h4>

                      <div className="flex items-center gap-4 text-xs text-gray-400 mb-2">
                        {episode.aired_date && (
                          <div className="flex items-center gap-1">
                            <Calendar size={12} />
                            <span>{formatDate(episode.aired_date)}</span>
                          </div>
                        )}
                        {episode.duration && (
                          <div className="flex items-center gap-1">
                            <Clock size={12} />
                            <span>{episode.duration} min</span>
                          </div>
                        )}
                      </div>

                      {episode.synopsis && (
                        <p className="text-sm text-gray-400 line-clamp-3 leading-relaxed">
                          {episode.synopsis}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default EpisodeList;
